import React, { useState, useContext } from 'react'
import axios from 'axios'
import MaterialTable from 'material-table'
import {
  Grid,
  IconButton,
  Dialog,
  TextField,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  InputLabel,
  Select,
  FormControl,
  MenuItem
} from '@material-ui/core'
import { AddBox, Edit, Delete } from '@material-ui/icons'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { Card, CardHeader, CardBody } from 'components/common/Card'
import Button from 'components/common/Button'
import PatchedPagination from 'components/common/PatchedPagination'
import BOLCheckoutModify from 'components/BOL/BOLCheckoutModify'
import { commas, noCommas, useForm } from 'helpers'
import { ReducerContext } from 'App'
import html2canvas from 'html2canvas'
import { useS3Upload, useConfig } from 'hooks/useConfig'
import LetterBOL from './LetterBOL'
import { isEthanolProduct, TEMPERATURE_MIN, TEMPERATURE_MAX } from 'constants/productTypes'
import 'assets/css/PrintBOLModal.css'

// Accepts a partially typed whole number, plus a decimal point so a fractional entry stays
// visible and can be flagged instead of being silently collapsed (12.5 -> 125)
const TEMPERATURE_INPUT_PATTERN = /^-?\d{0,3}(\.\d{0,2})?$/

const BolPreview = ({ bolData, tare, invInter }) => {
  const date = new Date().toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  })

  const bolQR = {
    site: bolData.terminal,
    date: date,
    carrier: bolData.carrier,
    truck: bolData.truck_number,
    driver: bolData.driver_name,
    sandType: bolData.sand_type,
    po: bolData.po_name
  }

  const total = invInter.reduce((sum, inv, idx) => {
    const weight = idx === 0 ? inv.gross_weight - tare : inv.gross_weight - invInter[idx - 1].gross_weight
    return sum + weight
  }, 0)

  const componentRef = React.useRef()

  return (
    <div
      id="bol-preview"
      style={{
        position: 'absolute',
        left: '-9999px',
        width: '816px', // 8.5 inches at 96 DPI
        backgroundColor: 'white',
        padding: '20px',
        fontFamily: 'Arial, sans-serif'
      }}
    >
      <LetterBOL
        componentRef={componentRef}
        date={date}
        bolQR={bolQR}
        total={total}
        invInters={invInter}
        bolData={{
          bolId: bolData.bolId,
          tare_weight: tare,
          processed_at: new Date().toISOString(),
          terminal: bolData.terminal,
          carrier: bolData.carrier,
          driver_name: bolData.driver_name,
          trailer_number: bolData.trailer_number,
          truck_number: bolData.truck_number,
          status: 'Completed',
          weighmaster_license: bolData.weighmaster_license || '',
          buyer: bolData.buyer || 'Chesapeake Operating L.L.C.',
          purchase_order_number: bolData.po_name,
          sand_type: bolData.sand_type,
          job_name: bolData.job_name || 'LIONETTI BRA PAD',
          destination_address: bolData.destination_address,
          truck_load_id: bolData.truck_load_id
        }}
      />
    </div>
  )
}

const BOLCheckout = ({
  invInter,
  inventoryData,
  activePOsData,
  getBOLs,
  sqlParams,
  loadInvInter,
  specificInvInterLoading,
  loadInventory,
  open,
  handleClose,
  printCheckboxHandler,
  specificPoData,
  specificBolData: {
    bolId,
    po_id,
    driver_name,
    truck_number,
    trailer_number,
    carrier,
    tare_weight,
    temperature,
    notes,
    sand_type,
    buyer,
    transloading_client,
    job_name,
    external_reference_number,
    source_fulfillment_id,
    truck_load_id
  }
}) => {
  const {
    state: {
      currentTerminal,
      user: { id: dbUserId, weighmaster_license }
    }
  } = useContext(ReducerContext)
  const { config } = useConfig()
  const { uploadToS3 } = useS3Upload()

  const [grossWeightInput, setGrossWeightInput] = useState('')
  const [meterReadingInput, setMeterReadingInput] = useState('')
  const [inventory_id, setInventory_id] = useState(null)
  const [printCheckbox, setPrintCheckbox] = useState(true)
  const [selectedPO, setSelectedPO] = useState(specificPoData)
  const [specificInvData, setSpecificInvData] = useState(null)
  const [invOpen, setInvOpen] = useState(false)
  const [invMod, setInvMod] = useState(null)
  const [isProcessing, setIsProcessing] = useState(false)

  const handleInvClose = () => setInvOpen(false)
  const handleClick = (invData, type) => {
    setSpecificInvData(invData)
    setInvOpen(true)
    setInvMod(type)
  }

  const bolInit = {
    po_id,
    driver_name,
    truck_number,
    trailer_number,
    carrier,
    tare_weight,
    temperature: temperature == null ? '' : String(temperature),
    notes,
    source_fulfillment_id,
    truck_load_id
  }

  const poInit = {
    sand_type,
    buyer,
    transloading_client,
    job_name,
    external_reference_number
  }
  const { setValue: setBOLValue, values: bolValues } = useForm(bolInit)
  const { setValue: setPOValue, values: poValues } = useForm(poInit)

  // Check if current PO is for ethanol (volume-based)
  const isEthanol = isEthanolProduct(poValues['sand_type'] || sand_type)

  const shippingWeight = ({ tableData: { id }, gross_weight }, tare_weight, inter) =>
    commas(id > 0 ? inter[id].gross_weight - inter[id - 1].gross_weight : gross_weight - tare_weight)

  // Calculate volume dispensed from cumulative meter readings
  const volumeDispensed = ({ tableData: { id }, volume_reading }, inter) => {
    const currentReading = Number(volume_reading) || 0
    const previousReading = id > 0 ? Number(inter[id - 1].volume_reading) || 0 : 0
    return commas(currentReading - previousReading)
  }

  // Columns for weight-based products (sand, cement)
  const weightColumns = [
    { title: 'Tare Weight', editable: 'never', render: () => commas(bolValues['tare_weight']) },
    {
      title: 'Net Weight',
      editable: 'never',
      render: row => commas(row.gross_weight - noCommas(bolValues['tare_weight']))
    },
    {
      title: 'Shipping Weight',
      editable: 'never',
      render: row => shippingWeight(row, noCommas(bolValues['tare_weight']), invInter)
    },
    {
      title: 'Gross Weight',
      field: 'gross_weight',
      render: row => (specificInvInterLoading ? <CircularProgress /> : commas(row.gross_weight))
    },
    { title: 'Time Stamp', field: 'created_at', hidden: true },
    {
      title: 'Inventory ID',
      field: 'inventory_id',
      ...(inventoryData.length === 0 && { render: () => <CircularProgress /> }),
      ...(inventoryData.length !== 0 && {
        render: rowdata => (specificInvInterLoading ? <CircularProgress /> : rowdata.inventory_id)
      })
    }
  ]

  // Columns for volume-based products (ethanol)
  const volumeColumns = [
    { title: 'Tare Weight', editable: 'never', render: () => commas(bolValues['tare_weight']) },
    {
      title: 'Net Gallons',
      field: 'volume_reading',
      render: row => (specificInvInterLoading ? <CircularProgress /> : commas(Number(row.volume_reading) || 0))
    },
    {
      title: 'Vol. Dispensed (gal)',
      editable: 'never',
      render: row => volumeDispensed(row, invInter)
    },
    {
      title: 'Gross Weight',
      field: 'gross_weight',
      render: row => (specificInvInterLoading ? <CircularProgress /> : commas(row.gross_weight))
    },
    { title: 'Time Stamp', field: 'created_at', hidden: true },
    {
      title: 'Tank Car ID',
      field: 'inventory_id',
      ...(inventoryData.length === 0 && { render: () => <CircularProgress /> }),
      ...(inventoryData.length !== 0 && {
        render: rowdata => (specificInvInterLoading ? <CircularProgress /> : rowdata.inventory_id)
      })
    }
  ]

  // Select columns based on product type
  const columns = isEthanol ? volumeColumns : weightColumns

  const handleInvSubmit = async e => {
    e.preventDefault()
    try {
      const item = {
        gross_weight: noCommas(grossWeightInput),
        inventory_id,
        bill_of_lading_id: bolId,
        terminal: currentTerminal,
        ...(isEthanol && meterReadingInput && { volume_reading: noCommas(meterReadingInput) })
      }

      await axios.post(`${config.LOGITRAC_API_URL}api/insert`, {
        item,
        dbTable: 'Inventory_Interactions',
        dbEvent: 'insert',
        dbUserId
      })
      loadInvInter({ bol_id: bolId })
    } catch (error) {
      console.log('error: ', error)
    }
    setGrossWeightInput('')
    setMeterReadingInput('')
  }

  const handleInvEdit = async (variables, action, isDirty) => {
    if (action === 'edit' && isDirty) {
      try {
        await axios.post(`${config.LOGITRAC_API_URL}api/update`, {
          array: [variables],
          dbTable: 'Inventory_Interactions',
          dbEvent: 'update',
          dbUnique: 'id',
          dbUserId
        })
        loadInvInter({ bol_id: bolId })
      } catch (error) {
        console.log('error: ', error)
      }
    }

    if (action === 'delete') {
      try {
        await axios.post(`${config.LOGITRAC_API_URL}api/update`, {
          array: [{ id: variables.id, archived_at: new Date().toISOString() }],
          dbTable: 'Inventory_Interactions',
          dbEvent: 'update',
          dbUnique: 'id',
          dbUserId
        })
        loadInvInter({ bol_id: bolId })
      } catch (error) {
        console.log('error: ', error)
      }
    }
  }

  const updateAllTerminalInventory = async () => {
    const distinctLocations = Array.from(new Set(invInter.map(i => i.inventory_id)))

    // Calculate shipped quantity per interaction
    // For ethanol: use volume_reading differences (gallons)
    // For sand/cement: use gross_weight differences (lbs)
    const shippings = invInter.reduce((acc, cur, ind, arr) => {
      const currentReading = isEthanol ? (Number(cur.volume_reading) || 0) : null
      const previousReading = isEthanol && ind > 0 ? (Number(arr[ind - 1].volume_reading) || 0) : 0
      const shippedQuantity = isEthanol
        ? currentReading - previousReading
        : ind > 0
          ? cur.gross_weight - arr[ind - 1].gross_weight
          : cur.gross_weight - (noCommas(bolValues['tare_weight']) || tare_weight)
      return [...acc, { shippedQuantity, inventory_id: cur.inventory_id }]
    }, [])

    const updated_at = new Date().toISOString()

    const updates = distinctLocations.map(location_id => {
      const totalShipped = shippings.reduce(
        (acc, cur) => (cur.inventory_id === location_id ? cur.shippedQuantity + acc : acc),
        0
      )
      const [location] = inventoryData.filter(item => item.inventory_id === location_id)
      return { inventory_id: location.inventory_id, quantity: location.quantity - totalShipped, updated_at }
    })

    try {
      const temp = await axios.post(`${config.LOGITRAC_API_URL}api/update`, {
        array: updates,
        dbTable: 'Terminal_Inventory',
        dbEvent: 'update',
        dbUnique: 'inventory_id',
        dbUserId
      })
      return temp
    } catch (error) {
      console.log('error: ', error)
    }
  }

  const handlePOSelect = e => {
    const [selectedPO] = activePOsData.filter(po => po.id === e.target.value)
    const { transloading_client, sand_type, id: po_id } = selectedPO
    setSelectedPO(selectedPO)
    setInventory_id(null)
    Object.entries(selectedPO).map(([key, val]) => setPOValue(key, val))
    setBOLValue('po_id', po_id)
    loadInventory({ transloading_client, sand_type })
  }

  // TODO: combine and convert all this to transactions
  const handleForm = async type => {
    setIsProcessing(true)
    try {
      const printData = await saveBOL(type === 'submit' ? 'completed' : 'started')
      if (type === 'submit') {
        await updateAllTerminalInventory()

        // Capture BOL image BEFORE closing dialog (if SandDrive integration needed)
        let bolImageBlob = null
        if (printData?.source_fulfillment_id) {
          try {
            const bolElement = document.getElementById('bol-preview')
            if (bolElement) {
              const canvas = await html2canvas(bolElement)
              bolImageBlob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.95))
            } else {
              console.error('BOL preview element not found')
            }
          } catch (error) {
            console.error('Failed to capture BOL preview:', error)
          }
        }

        try {
          printCheckboxHandler({ printCheckbox, printData: { ...printData, ...selectedPO } })
          handleClose()
        } catch (error) {
          console.error('Print handling error:', error)
          saveBOL('started')
        }

        // Handle SandDrive integration in background AFTER dialog closes
        if (printData?.source_fulfillment_id && bolImageBlob) {
          setTimeout(async () => {
            let syncAttempts = 0
            try {
              syncAttempts++
              const s3Url = await uploadBolToS3WithRetry(printData.bolId, bolImageBlob)

              const sentData = {
                source_fulfillment_id: printData.source_fulfillment_id,
                bol: String(printData.bolId),
                gross_weight: printData.gross_weight,
                measured_weight: printData.gross_weight - (noCommas(bolValues['tare_weight']) || tare_weight),
                og_permit: bolValues['og_permit'] || false,
                bol_loaded_url: s3Url,
                trailer_number: bolValues['trailer_number'] || ''
              }

              syncAttempts += 2 // Account for retries in the WithRetry functions
              await sendBolToSandDriveWithRetry(sentData)

              // Update sync status to success
              await updateBolSyncStatus(printData.bolId, 'success', null, syncAttempts)
              console.log(`BOL ${printData.bolId} successfully sent to SandDrive`)
            } catch (error) {
              console.error(`Failed to send BOL ${printData.bolId} to SandDrive after retries:`, error)
              // Extract error message from axios response
              const errorMessage =
                error.response?.data?.error ||
                error.response?.data?.message ||
                error.message ||
                'Failed to sync with SandDrive'

              // Update sync status to failed with error details
              await updateBolSyncStatus(printData.bolId, 'failed', errorMessage, syncAttempts || 3)
            }
          }, 100) // Small delay for cleanup
        }
      }
    } finally {
      setIsProcessing(false)
    }
  }

  const sendBolToSandDriveWithRetry = async (bolData, retries = 3) => {
    for (let i = 0; i < retries; i++) {
      try {
        const response = await axios.post(`${config.LOGITRAC_API_URL}api/proxy/sanddrive`, bolData)
        return response.data
      } catch (error) {
        if (i === retries - 1) throw error
        console.log(`Retry ${i + 1}/${retries} for SandDrive API...`)
        await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1))) // Exponential backoff
      }
    }
  }

  const uploadBolToS3WithRetry = async (bolId, imageBlob, retries = 3) => {
    for (let i = 0; i < retries; i++) {
      try {
        const file = new File([imageBlob], `bol-${bolId}.jpg`, { type: 'image/jpeg' })
        const publicUrl = await uploadToS3(file)
        return publicUrl
      } catch (error) {
        if (i === retries - 1) throw error
        console.log(`Retry ${i + 1}/${retries} for S3 upload...`)
        await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1))) // Exponential backoff
      }
    }
  }

  const updateBolSyncStatus = async (bolId, status, error = null, attempts = 0) => {
    try {
      await axios.post(`${config.LOGITRAC_API_URL}api/update`, {
        array: [
          {
            bolId,
            sanddrive_sync_status: status,
            sanddrive_sync_error: error,
            sanddrive_sync_attempts: attempts
          }
        ],
        dbTable: 'Bills_Of_Lading',
        dbEvent: 'update',
        dbUnique: 'bolId',
        dbUserId
      })
    } catch (error) {
      console.error('Failed to update BOL sync status:', error)
    }
  }

  const saveBOL = async status => {
    const { tare_weight, notes, temperature, ...rest } = bolValues
    // Never coerce a fractional value into the Int column: the UI blocks saving one, and this
    // keeps a non-integer from being silently rounded if it ever gets past the field
    const parsedTemperature =
      temperature === '' || temperature == null || !Number.isInteger(Number(temperature))
        ? null
        : Number(temperature)

    const update = {
      ...rest,
      bolId,
      tare_weight: typeof tare_weight === 'number' ? tare_weight : noCommas(tare_weight),
      ...(isEthanol && { temperature: parsedTemperature }),
      gross_weight: invInter.length ? Number(invInter[invInter.length - 1].gross_weight) : 0,
      status,
      user: String(dbUserId),
      terminal: currentTerminal,
      weighmaster_license,
      processed_at: new Date().toISOString(),
      notes: notes === '' ? null : notes,
      // Only initialize sync status for SandDrive-enabled BOLs when completing
      ...(rest.source_fulfillment_id && status === 'completed' && { sanddrive_sync_status: 'pending' })
    }

    try {
      await axios.post(`${config.LOGITRAC_API_URL}api/update`, {
        array: [update],
        dbTable: 'Bills_Of_Lading',
        dbEvent: 'update',
        dbUnique: 'bolId',
        dbUserId
      })
      getBOLs({ ...sqlParams, page_number: 0, currentTerminal })
      return update
    } catch (error) {
      console.log('error: ', error)
    }
  }

  // A typed decimal point is accepted into the field so the user can see it and be told why it
  // is rejected. Silently dropping it turned 12.5 into 125, which is a wrong temperature on a
  // shipping document rather than a visible mistake.
  const temperatureRaw = bolValues['temperature'] ?? ''
  const temperatureIsDecimal = /\./.test(temperatureRaw)
  const temperatureOutOfRange =
    Number(temperatureRaw) < TEMPERATURE_MIN || Number(temperatureRaw) > TEMPERATURE_MAX

  // Optional field, so only a value that is present and invalid blocks saving
  const temperatureError = isEthanol && !!temperatureRaw && (temperatureIsDecimal || temperatureOutOfRange)

  const textFields = [
    { disabled: true, width: 6, id: 'sand_type', label: 'Product', form: 'po' },
    { disabled: true, width: 6, id: 'buyer', label: 'Buyer', form: 'po' },
    { disabled: true, width: 6, id: 'transloading_client', label: 'Transloading Client', form: 'po' },
    { disabled: true, width: 6, id: 'job_name', label: 'Job Name', form: 'po' },
    { disabled: true, width: 6, id: 'external_reference_number', label: 'External Reference Number', form: 'po' },
    ...(bolValues['truck_load_id']
      ? [{ disabled: true, width: 6, id: 'truck_load_id', label: 'SandDrive Truck Load ID', form: 'bol' }]
      : []),
    { disabled: false, width: 12, id: 'driver_name', label: "Driver's Full Name", form: 'bol' },
    { disabled: false, width: 6, id: 'truck_number', label: 'Truck Number', form: 'bol' },
    { disabled: false, width: 6, id: 'trailer_number', label: 'Trailer Number', form: 'bol' },
    { disabled: false, width: 12, id: 'carrier', label: 'Carrier Name', form: 'bol' },
    {
      disabled: invInter.length !== 0,
      value: commas(bolValues['tare_weight']),
      width: 12,
      id: 'tare_weight',
      label: 'Tare Weight',
      onChange: e => setBOLValue(e.target.id, commas(e.target.value)),
      error:
        noCommas(bolValues['tare_weight']) < 15000 ||
        noCommas(bolValues['tare_weight']) > 80000 ||
        !bolValues['tare_weight'],
      helperText:
        noCommas(bolValues['tare_weight']) < 15000
          ? 'Tare Weight must be greater than 15,000 lbs'
          : noCommas(bolValues['tare_weight']) > 80000
          ? 'Tare Weight must be less than 80,000 lbs'
          : ''
    },
    ...(isEthanol
      ? [
          {
            disabled: false,
            width: 12,
            id: 'temperature',
            label: 'Temperature (°F)',
            form: 'bol',
            required: false,
            onChange: e => {
              const v = e.target.value
              TEMPERATURE_INPUT_PATTERN.test(v) && setBOLValue('temperature', v)
            },
            error: temperatureError,
            helperText: temperatureIsDecimal
              ? 'Whole degrees only — remove the decimal'
              : temperatureError
              ? `Temperature must be between ${TEMPERATURE_MIN} and ${TEMPERATURE_MAX} °F`
              : `Optional. Whole number from ${TEMPERATURE_MIN} to ${TEMPERATURE_MAX} °F.`
          }
        ]
      : []),
    { disabled: false, width: 12, id: 'notes', label: 'Notes', form: 'bol', required: false }
  ]

  const makeField = ({ id, label, width, disabled, onChange, error, helperText, value, form, required }) => {
    return (
      <Grid item xs={12} sm={width} md={width} className="GridItem" key={id}>
        <TextField
          id={id}
          label={label}
          value={value ? value : form === 'bol' ? bolValues[id] || '' : poValues[id] || ''}
          onChange={
            onChange
              ? onChange
              : form === 'bol'
              ? e => setBOLValue(e.target.id, e.target.value)
              : e => setPOValue(e.target.id, e.target.value)
          }
          margin="normal"
          fullWidth
          required={required !== false}
          error={error}
          helperText={helperText}
          disabled={disabled}
        />
      </Grid>
    )
  }

  return (
    <>
      <Dialog open={open} onClose={handleClose} fullWidth={true} maxWidth="xl">
        <Grid container spacing={1} className="addMarginLeft">
          <Grid item sm={12} md={6}>
            <Card>
              <CardHeader color="primary">
                <h4 className="cardTitleWhite">
                  {isEthanol ? 'Add Meter Reading & Weight' : 'Add New Gross Weight'} to BOL #{String(bolId)}
                </h4>
                <p className="cardCategoryWhite">
                  {isEthanol
                    ? 'Select a Tank Car, enter meter reading (gallons) and gross weight. Then click add (+).'
                    : 'Select an Inventory Location & enter the gross weight. Then click add (+).'}
                </p>
              </CardHeader>
              <CardBody>
                <form onSubmit={e => handleInvSubmit(e)}>
                  <Grid container className="GridContainer">
                    <Grid item xs={11} sm={isEthanol ? 3 : 5} md={isEthanol ? 3 : 5} className="GridItem">
                      <Autocomplete
                        id="inventory-location"
                        options={inventoryData.map(item => item.inventory_id)}
                        onChange={(_, inv_id) => setInventory_id(inv_id)}
                        value={inventory_id}
                        fullWidth
                        renderInput={params => (
                          <TextField
                            {...params}
                            label={isEthanol ? 'Tank Car ID' : 'Inventory Location'}
                            fullWidth
                            required
                          />
                        )}
                      />
                    </Grid>
                    {isEthanol && (
                      <Grid item xs={11} sm={3} md={3} className="GridItem">
                        <TextField
                          label="Net Gallons"
                          required
                          fullWidth
                          value={meterReadingInput}
                          error={
                            noCommas(meterReadingInput) <= (Number(invInter[invInter.length - 1]?.volume_reading) || 0)
                          }
                          helperText={
                            noCommas(meterReadingInput) <= (Number(invInter[invInter.length - 1]?.volume_reading) || 0)
                              ? 'Net gallons must be greater than previous reading'
                              : ''
                          }
                          onChange={e => setMeterReadingInput(commas(e.target.value))}
                        />
                      </Grid>
                    )}
                    <Grid item xs={11} sm={isEthanol ? 4 : 5} md={isEthanol ? 4 : 5} className="GridItem">
                      <TextField
                        label="Gross Weight (lbs)"
                        required
                        fullWidth
                        value={grossWeightInput}
                        error={
                          noCommas(grossWeightInput) <= noCommas(bolValues['tare_weight']) ||
                          noCommas(grossWeightInput) > 150000 ||
                          noCommas(grossWeightInput) <= invInter[invInter.length - 1]?.gross_weight
                        }
                        helperText={
                          noCommas(grossWeightInput) <= noCommas(bolValues['tare_weight']) ||
                          noCommas(grossWeightInput) <= invInter[invInter.length - 1]?.gross_weight
                            ? 'Gross weight must be greater than tare weight or most recent gross weight'
                            : noCommas(grossWeightInput) > 150000
                            ? 'Gross weight is too high.'
                            : ''
                        }
                        onChange={e => setGrossWeightInput(commas(e.target.value))}
                      />
                    </Grid>
                    <Grid
                      item
                      xs={1}
                      sm={1}
                      md={1}
                      className="GridItem"
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                      <IconButton
                        aria-label="add interaction"
                        size="medium"
                        type="submit"
                        disabled={
                          noCommas(grossWeightInput) <= noCommas(bolValues['tare_weight']) ||
                          noCommas(grossWeightInput) > 150000 ||
                          noCommas(grossWeightInput) <= invInter[invInter.length - 1]?.gross_weight ||
                          (isEthanol &&
                            (!meterReadingInput ||
                              noCommas(meterReadingInput) <=
                                (Number(invInter[invInter.length - 1]?.volume_reading) || 0)))
                        }
                      >
                        <AddBox fontSize="inherit" />
                      </IconButton>
                    </Grid>
                  </Grid>
                </form>
              </CardBody>
            </Card>

            {specificInvInterLoading ? (
              <CircularProgress />
            ) : (
              <MaterialTable
                title="Finalize Bill of Lading"
                columns={columns}
                data={invInter}
                options={{
                  search: false,
                  headerStyle: {
                    backgroundColor: '#43a047',
                    color: '#FFF',
                    textAlign: 'left',
                    marginTop: '30px',
                    marginLeft: '8px'
                  }
                }}
                actions={[{ icon: '', onClick: () => {} }]} //if removed Action component below won't render
                components={{
                  Action: props => (
                    <>
                      {
                        <>
                          <IconButton onClick={() => handleClick(props.data, 'edit')}>
                            <Edit />
                          </IconButton>
                          <IconButton onClick={() => handleClick(props.data, 'delete')}>
                            <Delete />
                          </IconButton>
                        </>
                      }
                    </>
                  ),
                  Pagination: PatchedPagination
                }}
              />
            )}
          </Grid>
          <Grid item sm={12} md={6}>
            <Grid container className="GridContainer">
              <Grid item xs={12} className="GridItem">
                <Card>
                  <CardHeader color="primary">
                    <h4 className="cardTitleWhite">Bill of Lading ID #{bolId}</h4>
                    <p className="cardCategoryWhite">
                      Please confirm these details are correct before finalizing this BOL.
                    </p>
                  </CardHeader>
                  <CardBody>
                    <Grid container className="GridContainer">
                      <Grid item xs={12} sm={6} md={6} className="GridItem">
                        <FormControl fullWidth required style={{ margin: '16px 0 8px' }}>
                          <InputLabel>Purchase Order Number</InputLabel>
                          <Select
                            name="purchase_order"
                            onChange={handlePOSelect}
                            fullWidth
                            value={bolValues['po_id']}
                            required
                            disabled={invInter.length !== 0}
                          >
                            {activePOsData.map(PurchaseOrder => (
                              <MenuItem key={PurchaseOrder.id} value={PurchaseOrder.id}>
                                {PurchaseOrder.purchase_order_number}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      {textFields.map(field => makeField(field))}
                      <Grid item className="GridItem">
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={printCheckbox}
                              color="primary"
                              onChange={e => setPrintCheckbox(e.target.checked)}
                              name="printCheckbox"
                            />
                          }
                          label="Print BOLs"
                        />
                      </Grid>
                    </Grid>
                    <Button
                      color="primary"
                      type="button"
                      onClick={() => handleForm('submit')}
                      disabled={
                        isProcessing ||
                        invInter.length < 1 ||
                        noCommas(bolValues['tare_weight']) < 15000 ||
                        noCommas(bolValues['tare_weight']) > 80000 ||
                        !bolValues['tare_weight'] ||
                        temperatureError
                      }
                    >
                      {isProcessing ? (
                        <>
                          <CircularProgress size={16} style={{ marginRight: 8, color: 'white' }} />
                          Processing...
                        </>
                      ) : invInter.length < 1 ? (
                        'No Inventory Interactions Found'
                      ) : (
                        'Process BOL'
                      )}
                    </Button>
                    <Button
                      color="white"
                      type="button"
                      onClick={() => handleForm('save')}
                      disabled={
                        isProcessing ||
                        noCommas(bolValues['tare_weight']) < 15000 ||
                        noCommas(bolValues['tare_weight']) > 80000 ||
                        !bolValues['tare_weight'] ||
                        temperatureError
                      }
                    >
                      {isProcessing ? 'Processing...' : 'Save'}
                    </Button>
                  </CardBody>
                </Card>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Dialog>
      {invOpen && (
        <BOLCheckoutModify
          open={invOpen}
          invMod={invMod}
          handleClose={handleInvClose}
          invInter={invInter}
          inventoryData={inventoryData.map(item => item.inventory_id)}
          tare_weight={bolValues['tare_weight']}
          specificInvData={specificInvData}
          shippingWeight={shippingWeight}
          handleInvEdit={handleInvEdit}
          sand_type={poValues['sand_type'] || sand_type}
        />
      )}
      <BolPreview
        bolData={{
          po_name: selectedPO?.purchase_order_number || poValues['purchase_order_number'],
          bolId,
          po_id: bolValues['po_id'],
          driver_name: bolValues['driver_name'],
          truck_number: bolValues['truck_number'],
          trailer_number: bolValues['trailer_number'],
          carrier: bolValues['carrier'],
          sand_type: poValues['sand_type'],
          gross_weight: invInter.length ? Number(invInter[invInter.length - 1].gross_weight) : 0,
          terminal: currentTerminal,
          buyer: poValues['buyer'] || 'Chesapeake Operating L.L.C.',
          job_name: poValues['job_name'] || 'LIONETTI BRA PAD',
          destination_address: selectedPO?.destination_address || poValues['destination_address'],
          weighmaster_license: bolValues['weighmaster_license'] || '',
          truck_load_id: bolValues['truck_load_id']
        }}
        inventoryData={inventoryData}
        tare={noCommas(bolValues['tare_weight']) || tare_weight}
        invInter={invInter}
      />
    </>
  )
}

export default BOLCheckout
